import { Box, Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid";
import React, { ChangeEvent, FC, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { ROWS_PER_PAGE } from "../../../utils/constants";
import { useCheckOtsStatusQuery, useGetCamerasListQuery } from "../../../redux/reducers/settings.reducer";
import { useNavigate } from "react-router-dom";
import { ICameraDetails, IGetCamerasListRequestData } from "../../../interfaces";
import CameraSetupDialog from "../../../common/CameraSetup";

// interface IOTSCameraDetails {
// 	id: number;
// 	name: string;
// 	camera_type: "Entry" | "Exit";
// 	camera_id: number;
// 	site_id: number;
// 	rtsp: string;
// 	settings_url: string;
// }

const CamerasList: FC = () => {
	const navigate = useNavigate();

	const searchInputRef = useRef<HTMLInputElement>(null);

	const [searchInput, setSearchInput] = useState<string>("");
	const [showCameraDetailsDialog, setShowCameraDetailsDialog] = useState<boolean>(false);
	const [selectedCameraDetails, setSelectedCameraDetails] = useState<ICameraDetails | null>(null);
	const [filters, setFilters] = useState<IGetCamerasListRequestData>({});

	// APIS
	const { data: siteConfig } = useCheckOtsStatusQuery(null);

	const {
		isLoading: getCamerasListLoading,
		isFetching: getCamerasListFetching,
		data: getCamerasListResponse,
		originalArgs: getCamerasListArgs,
		refetch: refetchCamerasList
	} = useGetCamerasListQuery(filters);

	const camerasList = getCamerasListResponse?.results ?? [];

	function handleChangeSearchInput(event: ChangeEvent<HTMLInputElement>) {
		setSearchInput(event.target.value);
	}

	function handleSubmitSearch(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };

			if (searchInput) updatedFilters.site_name = searchInput;
			else delete updatedFilters.site_name;

			return updatedFilters;
		});
	}

	function handleClearSearchInput() {
		setFilters((currentFiltersState) => {
			const updatedFilters = { ...currentFiltersState };
			delete updatedFilters.site_name;
			return updatedFilters;
		});

		setSearchInput("");

		if (searchInputRef.current) searchInputRef.current.focus();
	}

	function handleOpenCameraDetailsDialog(cameraDetails: ICameraDetails | null) {
		setSelectedCameraDetails(cameraDetails);
		setShowCameraDetailsDialog(true);
	}

	function handleCloseCameraDetailsDialog() {
		setShowCameraDetailsDialog(false);
		setSelectedCameraDetails(null);
		refetchCamerasList();
	}

	const goToAddCameraScreen = useCallback(() => {
		navigate("/ots/add-camera");
	}, [navigate]);

	function handleSaveButtonClick() {
		navigate("/ots/finalize");
	}

	useEffect(() => {
		if (
			!getCamerasListLoading &&
			!getCamerasListFetching &&
			getCamerasListResponse &&
			getCamerasListResponse.results.length <= 0 &&
			(!getCamerasListArgs || !getCamerasListArgs.site_name)
		) {
			goToAddCameraScreen();
		}
	}, [getCamerasListArgs, getCamerasListFetching, getCamerasListLoading, getCamerasListResponse, goToAddCameraScreen]);

	const columnsData: GridColDef[] = [
		{ field: "camera_site", headerName: "Name", minWidth: 200, flex: 1, sortable: false },
		{
			field: "is_entry",
			headerName: "Camera Type",
			minWidth: 150,
			flex: 1,
			sortable: false,
			valueGetter: (params: GridValueGetterParams<ICameraDetails, boolean>) => (params.value ? "Entry" : "Exit")
		},
		{ field: "camera_id", headerName: "Camera ID", minWidth: 100, flex: 1, sortable: false },
		{ field: "site_id", headerName: "Site ID", minWidth: 100, flex: 1, sortable: false },
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<ICameraDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton color="info" size="small" onClick={() => handleOpenCameraDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="small" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="ots-screen-content-wrapper">
			<Box className="ots-cameras-list-screen-content">
				<Box className="ots-cameras-list-screen-header">
					<Box component="form" noValidate onSubmit={handleSubmitSearch}>
						{siteConfig && siteConfig.device_type !== "rpi" ? (
							<TextField
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								placeholder="Search"
								autoComplete="off"
								inputRef={searchInputRef}
								sx={{ width: "350px" }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											{filters.site_name ? (
												<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
													<CloseIcon fontSize="inherit" />
												</IconButton>
											) : null}

											<IconButton type="submit" edge="end">
												<SearchIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									)
								}}
							/>
						) : null}
					</Box>

					{siteConfig && siteConfig.device_type !== "rpi" ? (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							sx={{ textTransform: "capitalize" }}
							startIcon={<AddIcon />}
							onClick={goToAddCameraScreen}
						>
							Add New Camera
						</Button>
					) : null}
				</Box>

				<Box className="ots-cameras-list-table-wrapper">
					<DataGrid
						columns={columnsData}
						rows={camerasList}
						disableColumnMenu
						disableRowSelectionOnClick
						density="compact"
						rowSpacingType="border"
						columnHeaderHeight={80}
						pageSizeOptions={[ROWS_PER_PAGE]}
						loading={getCamerasListLoading || getCamerasListFetching}
						classes={{
							columnSeparator: "ots-cameras-list-table-column-separator",
							columnHeader: "ots-cameras-list-table-column-header",
							root: "ots-cameras-list-table-root"
						}}
					/>
				</Box>
			</Box>

			<Box className="ots-form-actions-wrapper">
				{/* <Button variant="outlined" color="error" sx={{ paddingX: 5 }}>
					Back
				</Button> */}

				<Button disableElevation variant="contained" color="primary" onClick={handleSaveButtonClick}>
					Save and Next
				</Button>
			</Box>

			<CameraSetupDialog
				open={showCameraDetailsDialog}
				cameraDetails={selectedCameraDetails}
				onClose={handleCloseCameraDetailsDialog}
			/>
		</Box>
	);
};

export default CamerasList;
